import { useState } from "react"
import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
} from "@headlessui/react"
import { Bars3Icon, XMarkIcon } from "./icons";
import { LangLink } from "./common/LangLink";
import { UserPrefsComponent } from "./UserPrefsComponent";
import { MyAccountComponent } from "./MyAccountComponent";
import { useTranslation } from "react-i18next";
import { Form, useLocation, useRouteLoaderData, useSearchParams } from "@remix-run/react";
import type { RootData } from "~/root";
import { redirectToLoginUrl, redirectToRegister, useRootLoaderData, useRouteHandle } from "~/apis/utils";
import { getLogoAssets } from "~/services/site-assets";

export function Navbar({ siteId, showVoucherTab }: { siteId: number, showVoucherTab: boolean }) {
	const [open, setOpen] = useState(false)
	const { t } = useTranslation();
	const logo = getLogoAssets(siteId);
	const { user } = useRootLoaderData();
	const { hideUserPrefs } = useRouteHandle();
	return <>
		<MobileMenu siteId={siteId} showVoucherTab={showVoucherTab} open={open} setOpen={setOpen} />
		<nav className="z-10 flex gap-x-2 items-center justify-between py-4">
			<div className="flex items-center justify-between w-full lg:w-auto">
				<LangLink to="/">
					<img className="logo-main" src={logo} alt="eTopupOnline.com" />
				</LangLink>
				{hideUserPrefs ? null : <div className="lg:hidden">
					<UserPrefsComponent />
				</div>}
				<button
					type="button"
					onClick={() => setOpen(true)}
					className="relative rounded-md bg-white p-2 text-gray-400 lg:hidden"
				>
					<span className="absolute -inset-0.5" />
					<span className="sr-only">{t("common.open_menu")}</span>
					<Bars3Icon aria-hidden="true" className="size-6" />
				</button>
			</div>
			<div className="hidden lg:flex flex-1 items-center">
				{user ? <LangLink to="/dashboard" className="nav-item">
					<h3 className="font-semibold text-sm">{t("common.dashboard")}</h3>
				</LangLink> : null}
				<LangLink to="/shop" className="nav-item" >
					<h2 className="font-semibold text-sm">{t("common.shop")}</h2>
				</LangLink>
				{showVoucherTab ? <LangLink to="/voucher" className="nav-item" >
					<h2 className="font-semibold text-sm">{t("common.voucher")}</h2>
				</LangLink> : null}
				<LangLink to="/auto-topup" className="nav-item">
					<span className="font-semibold text-sm">{t("common.auto_topup")}</span>
				</LangLink>
			</div>
			<div className="hidden lg:flex items-center gap-x-3">
				{hideUserPrefs ? null : <UserPrefsComponent />}
				{hideUserPrefs ? null : <span className="border-r border-gray-300 w-px">&nbsp;</span>}
				<MyAccountComponent />
			</div>
		</nav>
	</>
}
function MobileMenu({ siteId, showVoucherTab, open, setOpen }: { siteId: number, showVoucherTab: boolean, open: boolean, setOpen: (value: boolean) => void }) {
	const { t } = useTranslation();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const { user } = useRootLoaderData();
	const logo = getLogoAssets(siteId);
	return <Dialog open={open} onClose={setOpen} className="relative z-40 lg:hidden">
		<DialogBackdrop
			transition
			className="fixed inset-0 bg-black/25 transition-opacity duration-300 ease-linear data-[closed]:opacity-0" />
		<div className="fixed inset-0 flex justify-end">
			<DialogPanel
				transition
				className="relative flex w-full flex-col overflow-y-auto bg-white pb-12 transition duration-300 ease-in-out data-[closed]:translate-x-full"
			>
				<div className="p-4 w-full bg-white flex gap-x-6 items-center justify-between sticky top-0">
					<LangLink to="/">
						<img className="logo-main" src={logo} alt="eTopupOnline.com" />
					</LangLink>
					<button
						type="button"
						onClick={() => setOpen(false)}
						className="relative rounded-md bg-white p-2 text-gray-400 lg:hidden"
					>
						<span className="absolute -inset-0.5" />
						<span className="sr-only">Close menu</span>
						<XMarkIcon aria-hidden="true" className="size-6" />
					</button>
				</div>
				<div className="flex flex-col px-4 py-2">
					{user ? <LangLink to="/dashboard" className="px-1 py-2" onClick={e => setOpen(false)}>
						<h3 className="font-semibold text-slate-700">{t("common.dashboard")}</h3>
					</LangLink> : null}
					<LangLink to="/shop" className="px-1 py-2" onClick={e => setOpen(false)} >
						<h2 className="font-semibold text-slate-700">{t("common.shop")}</h2>
					</LangLink>
					{showVoucherTab ? <LangLink to="/voucher" className="px-1 py-2" onClick={e => setOpen(false)}>
						<h2 className="font-semibold text-slate-700">{t("common.voucher")}</h2>
					</LangLink> : null}
					<LangLink to="/auto-topup" className="px-1 py-2" onClick={e => setOpen(false)}>
						<span className="font-semibold text-slate-700">{t("common.auto_topup")}</span>
					</LangLink>
					<LangLink to="/blogs" className="px-1 py-2" onClick={e => setOpen(false)}>
						<span className="font-semibold text-slate-700">{t("common.blogs")}</span>
					</LangLink>
					<hr className="my-2" />
					{user ? null : <div className="flex flex-col">
						<LangLink to={`${redirectToLoginUrl(location, searchParams)}`} className="px-1 py-2" onClick={e => setOpen(false)}>
							<span className="font-semibold text-slate-700">{t("common.login")}</span>
						</LangLink>
						<LangLink to={`${redirectToRegister(location, searchParams)}`} className="px-1 py-2" onClick={e => setOpen(false)}>
							<span className="font-semibold text-slate-700">{t("common.register")}</span>
						</LangLink>
					</div>}
					{user ? <div className="flex flex-col">
						<span className="px-1 py-2">{user.fullName}</span>
						<LangLink to="/account-settings" className="p-2 pl-4" onClick={e => setOpen(false)}>
							<span className="font-semibold">{t("common.account_settings")}</span>
						</LangLink>
						{!user.authenticationmethod ? <LangLink to="/reset-password" className="p-2 pl-4" onClick={e => setOpen(false)}>
							<span className="font-semibold">{t("common.reset_password")}</span>
						</LangLink> : null}
						<Form action="/logout" method="POST">
							<button className="p-2 pl-4">
								<span className="font-semibold">{t("common.sign_out")}</span>
							</button>
						</Form>
					</div> : null}
				</div>
			</DialogPanel>
		</div>
	</Dialog>
}
